import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../../api.ts';
import { formatEuros } from '../../format.ts';

interface Settings {
  referral: { defaultSharePct: number; holdDays: number; minPayoutCents: number; vatRatePct: number };
  extras: { dependenceThresholdPct: number; dependenceWarnPct: number; dependenceGraceDays: number; dependenceMinRevenueCents: number; dependenceEnforce: boolean; vatRatePct: number; minExperiences: number };
  reliability: Record<string, number>;
  ratings: { revealAfterDays: number; lowScoreThreshold: number; consecutiveLowAlert: number; priorScore: number; priorWeight: number; minRatingsToDisplay: number; minTagCount: number; lateToleranceMin: number };
  matching: { weights: Record<string, number>; coversBlockRatio: number; aloneMinSeniority: string; blockOnRequired: boolean; urgentHours: number };
  company: { legalName: string; address: string; postalCode: string; city: string; siret: string; vatNumber: string };
  notifications: { supportEmail: string };
}

const ERRORS: Record<string, string> = {
  INVALID_SHARE_PCT: 'Le taux doit être compris entre 0 et 100.',
  INVALID_HOLD_DAYS: 'Le délai doit être un nombre entier de jours entre 0 et 90.',
  INVALID_MIN_PAYOUT: 'Le minimum de versement doit être un montant positif.',
  INVALID_VAT_RATE: 'Le taux de TVA doit être compris entre 0 et 30.',
  INVALID_SIRET: 'Le SIRET doit comporter 14 chiffres.',
  INVALID_DEPENDENCE_THRESHOLD: 'Le seuil de dépendance doit être entre 1 et 100 %, et le seuil d’avertissement ne peut pas le dépasser.',
  INVALID_GRACE_DAYS: 'Le délai de régularisation doit être un nombre entier de jours entre 0 et 365.',
  INVALID_MIN_REVENUE: 'Le chiffre d’affaires minimal doit être un montant positif.',
  INVALID_ENFORCE: 'Valeur invalide pour le blocage.',
  INVALID_RELIABILITY_SETTING: 'Un réglage de fiabilité est hors limites.',
  INVALID_RATINGS_SETTING: 'Un réglage de notation est hors limites.',
  INVALID_MATCHING_WEIGHT: 'Chaque poids doit être un nombre entre 0 et 100.',
  INVALID_MATCHING: 'Un réglage du matching est invalide.',
  INVALID_SUPPORT_EMAIL: 'Adresse e-mail du support invalide.',
  INVALID_COMPANY: 'Une des informations de la société est invalide.',
};

function Hint({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>{children}</div>;
}

const WEIGHT_LABELS: Record<string, string> = {
  role: 'Poste',
  seniority: 'Séniorité',
  pos: 'Logiciel de caisse',
  reservation: 'Logiciel de réservation',
  covers: 'Volume de couverts',
  shifts: 'Disponibilités',
  languages: 'Langues',
  certifications: 'Certifications',
  equipment: 'Équipement (tenue…)',
  alone: 'Autonomie (mission en solo)',
  trackRecord: 'Historique sur le poste (urgent)',
  reliability: 'Fiabilité (urgent)',
};

export function AdminSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [referral, setReferral] = useState({ defaultSharePct: '', holdDays: '', minPayoutEuros: '', vatRatePct: '' });
  const [extras, setExtras] = useState({ thresholdPct: '', warnPct: '', graceDays: '', minRevenueEuros: '', enforce: true, vatRatePct: '', minExperiences: '1' });
  const [matching, setMatching] = useState<{ weights: Record<string, string>; coversBlockPct: string; aloneMinSeniority: string; blockOnRequired: boolean; urgentHours: string } | null>(null);
  const [ratings, setRatings] = useState<Record<string, string> | null>(null);
  const [reliability, setReliability] = useState<Record<string, string> | null>(null);
  const [company, setCompany] = useState<Settings['company'] | null>(null);
  const [supportEmail, setSupportEmail] = useState('');
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  function hydrate(s: Settings) {
    setSettings(s);
    setReferral({
      defaultSharePct: String(s.referral.defaultSharePct),
      holdDays: String(s.referral.holdDays),
      minPayoutEuros: String(s.referral.minPayoutCents / 100),
      vatRatePct: String(s.referral.vatRatePct),
    });
    setExtras({
      thresholdPct: String(s.extras.dependenceThresholdPct),
      warnPct: String(s.extras.dependenceWarnPct),
      graceDays: String(s.extras.dependenceGraceDays),
      minRevenueEuros: String(s.extras.dependenceMinRevenueCents / 100),
      enforce: s.extras.dependenceEnforce,
      vatRatePct: String(s.extras.vatRatePct),
      minExperiences: String(s.extras.minExperiences),
    });
    setMatching({
      weights: Object.fromEntries(Object.entries(s.matching.weights).map(([k, v]) => [k, String(v)])),
      coversBlockPct: String(Math.round(s.matching.coversBlockRatio * 100)),
      aloneMinSeniority: s.matching.aloneMinSeniority,
      blockOnRequired: s.matching.blockOnRequired,
      urgentHours: String(s.matching.urgentHours),
    });
    setReliability(Object.fromEntries(Object.entries(s.reliability).map(([k, v]) => [k, String(v)])));
    setRatings(Object.fromEntries(Object.entries(s.ratings).map(([k, v]) => [k, String(v)])));
    setCompany(s.company);
    setSupportEmail(s.notifications.supportEmail);
  }

  useEffect(() => {
    api.get<{ settings: Settings }>('/admin/settings').then((res) => hydrate(res.settings));
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.put<{ settings: Settings }>('/admin/settings', {
        referral: {
          defaultSharePct: Number(referral.defaultSharePct),
          holdDays: Number(referral.holdDays),
          minPayoutCents: Math.round(Number(referral.minPayoutEuros) * 100),
          vatRatePct: Number(referral.vatRatePct),
        },
        extras: {
          dependenceThresholdPct: Number(extras.thresholdPct),
          dependenceWarnPct: Number(extras.warnPct),
          dependenceGraceDays: Number(extras.graceDays),
          dependenceMinRevenueCents: Math.round(Number(extras.minRevenueEuros) * 100),
          dependenceEnforce: extras.enforce,
          vatRatePct: Number(extras.vatRatePct),
          minExperiences: Number(extras.minExperiences),
        },
        matching: matching && {
          weights: Object.fromEntries(Object.entries(matching.weights).map(([k, v]) => [k, Number(v)])),
          coversBlockRatio: Number(matching.coversBlockPct) / 100,
          aloneMinSeniority: matching.aloneMinSeniority,
          blockOnRequired: matching.blockOnRequired,
          urgentHours: Number(matching.urgentHours),
        },
        reliability: reliability && Object.fromEntries(Object.entries(reliability).map(([k, v]) => [k, Number(v)])),
        ratings: ratings && Object.fromEntries(Object.entries(ratings).map(([k, v]) => [k, Number(v)])),
        company,
        notifications: { supportEmail },
      });
      hydrate(res.settings);
      setMessage({ kind: 'ok', text: 'Paramètres enregistrés. Ils s’appliquent aux prochaines opérations.' });
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? ERRORS[err.code] ?? 'Enregistrement impossible.' : 'Enregistrement impossible.' });
    } finally {
      setSaving(false);
    }
  }

  if (!settings || !company || !matching || !ratings || !reliability) return <div className="empty-state">Chargement…</div>;
  const setC = (patch: Partial<Settings['company']>) => setCompany((c) => (c ? { ...c, ...patch } : c));
  const companyComplete = Boolean(company.legalName && company.address && company.postalCode && company.city && /^\d{14}$/.test(company.siret.replace(/\s/g, '')));

  return (
    <form onSubmit={save} style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Commissions d'apport</div>

        <div>
          <label className="field-label" htmlFor="share">Part de notre commission reversée à l'apporteur (%)</label>
          <input id="share" className="input" type="number" min={0} max={100} step="0.5" value={referral.defaultSharePct} onChange={(e) => setReferral({ ...referral, defaultSharePct: e.target.value })} />
          <Hint>Taux global. Chaque apporteur peut avoir un taux propre (page « Apporteurs »), qui prime. Une modification ne s'applique qu'aux affaires clôturées ensuite : les commissions déjà calculées gardent leur taux.</Hint>
        </div>

        <div>
          <label className="field-label" htmlFor="hold">Délai de carence avant versement (jours)</label>
          <input id="hold" className="input" type="number" min={0} max={90} step={1} value={referral.holdDays} onChange={(e) => setReferral({ ...referral, holdDays: e.target.value })} />
          <Hint>Fenêtre pendant laquelle une commission reste non payable (litige, remboursement), à partir de la clôture de l'affaire.</Hint>
        </div>

        <div>
          <label className="field-label" htmlFor="min">Minimum de versement (€ HT)</label>
          <input id="min" className="input" type="number" min={0} step="1" value={referral.minPayoutEuros} onChange={(e) => setReferral({ ...referral, minPayoutEuros: e.target.value })} />
          <Hint>Montant cumulé de commissions payables à partir duquel un apporteur peut demander sa facture et son versement (actuellement {formatEuros(settings.referral.minPayoutCents)}).</Hint>
        </div>

        <div>
          <label className="field-label" htmlFor="vat">Taux de TVA des apporteurs assujettis (%)</label>
          <input id="vat" className="input" type="number" min={0} max={30} step="0.1" value={referral.vatRatePct} onChange={(e) => setReferral({ ...referral, vatRatePct: e.target.value })} />
          <Hint>Ajouté aux commissions (traitées comme des montants HT) pour les apporteurs assujettis à la TVA. Les apporteurs en franchise n'en facturent pas. À valider avec votre expert-comptable.</Hint>
        </div>
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Extras : dépendance économique</div>

        <div>
          <label className="field-label" htmlFor="dep-threshold">Plafond par client (% du chiffre d'affaires)</label>
          <input id="dep-threshold" className="input" type="number" min={1} max={100} step="1" value={extras.thresholdPct} onChange={(e) => setExtras({ ...extras, thresholdPct: e.target.value })} />
          <Hint>Un extra dont un seul client dépasse cette part de son CA encaissé sur 12 mois glissants est en situation de dépendance.</Hint>
        </div>

        <div>
          <label className="field-label" htmlFor="dep-warn">Seuil d'avertissement (%)</label>
          <input id="dep-warn" className="input" type="number" min={0} max={100} step="1" value={extras.warnPct} onChange={(e) => setExtras({ ...extras, warnPct: e.target.value })} />
          <Hint>À partir de cette part, l'extra voit un avertissement sans conséquence.</Hint>
        </div>

        <div>
          <label className="field-label" htmlFor="dep-grace">Délai de régularisation (jours)</label>
          <input id="dep-grace" className="input" type="number" min={0} max={365} step="1" value={extras.graceDays} onChange={(e) => setExtras({ ...extras, graceDays: e.target.value })} />
          <Hint>Après le dépassement, l'extra a ce délai pour rééquilibrer avant le blocage. Il repart de zéro si le client principal change.</Hint>
        </div>

        <div>
          <label className="field-label" htmlFor="dep-min">CA minimal pour évaluer la règle (€)</label>
          <input id="dep-min" className="input" type="number" min={0} step="100" value={extras.minRevenueEuros} onChange={(e) => setExtras({ ...extras, minRevenueEuros: e.target.value })} />
          <Hint>Sous ce chiffre d'affaires sur 12 mois, la règle n'est pas évaluée : un extra qui débute n'a forcément qu'un client.</Hint>
        </div>

        <label style={{ display: 'flex', gap: 8, fontSize: 13.5, fontWeight: 600, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={extras.enforce} onChange={(e) => setExtras({ ...extras, enforce: e.target.checked })} style={{ marginTop: 3 }} />
          <span>
            Bloquer les candidatures chez le client dominant une fois le délai écoulé
            <Hint>Décoché : alerte seule, aucun blocage. Le blocage ne vise que le client dominant, les autres établissements restent accessibles.</Hint>
          </span>
        </label>

        <div>
          <label className="field-label" htmlFor="min-exp">Expériences avec référent exigées pour candidater</label>
          <input id="min-exp" className="input" type="number" min={0} max={5} step="1" value={extras.minExperiences} onChange={(e) => setExtras({ ...extras, minExperiences: e.target.value })} />
          <Hint>Nombre d'expériences (avec les coordonnées d'un référent) que l'extra doit déclarer avant sa première candidature. Seule la déclaration compte, pas la réponse du référent. 0 : aucune exigence.</Hint>
        </div>

        <div>
          <label className="field-label" htmlFor="extra-vat">Taux de TVA des extras assujettis (%)</label>
          <input id="extra-vat" className="input" type="number" min={0} max={30} step="0.1" value={extras.vatRatePct} onChange={(e) => setExtras({ ...extras, vatRatePct: e.target.value })} />
          <Hint>Pour les extras assujettis à la TVA, le montant payé par l'établissement est considéré TTC : la TVA en est extraite sur la facture. À valider avec votre expert-comptable.</Hint>
        </div>
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Matching missions et extras</div>
        <Hint>Le score de compatibilité compare les exigences de la mission au profil de l'extra. Seuls les critères que la mission précise comptent : les poids sont relatifs entre eux.</Hint>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
          {Object.entries(WEIGHT_LABELS).map(([key, text]) => (
            <div key={key}>
              <label className="field-label" htmlFor={`w-${key}`}>{text}</label>
              <input id={`w-${key}`} className="input" type="number" min={0} max={100} step="1" value={matching.weights[key] ?? ''} onChange={(e) => setMatching({ ...matching, weights: { ...matching.weights, [key]: e.target.value } })} />
            </div>
          ))}
        </div>

        <label style={{ display: 'flex', gap: 8, fontSize: 13.5, fontWeight: 600, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={matching.blockOnRequired} onChange={(e) => setMatching({ ...matching, blockOnRequired: e.target.checked })} style={{ marginTop: 3 }} />
          <span>
            Empêcher de candidater quand une exigence obligatoire n'est pas remplie
            <Hint>Décoché : l'écart reste affiché dans le score, mais l'extra peut candidater.</Hint>
          </span>
        </label>

        <div>
          <label className="field-label" htmlFor="covers-ratio">Seuil de couverts (% de la capacité demandée)</label>
          <input id="covers-ratio" className="input" type="number" min={0} max={100} step="5" value={matching.coversBlockPct} onChange={(e) => setMatching({ ...matching, coversBlockPct: e.target.value })} />
          <Hint>Un extra dont la capacité déclarée est inférieure à ce pourcentage du nombre de couverts attendus est écarté.</Hint>
        </div>

        <div>
          <label className="field-label" htmlFor="alone-min">Séniorité attendue pour une mission en solo</label>
          <select id="alone-min" className="input" value={matching.aloneMinSeniority} onChange={(e) => setMatching({ ...matching, aloneMinSeniority: e.target.value })}>
            <option value="JUNIOR">Junior</option>
            <option value="CONFIRME">Confirmé</option>
            <option value="EXPERT">Expert</option>
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="urgent-hours">Mission urgente si elle démarre dans moins de (heures)</label>
          <input id="urgent-hours" className="input" type="number" min={0} max={24} step="1" value={matching.urgentHours} onChange={(e) => setMatching({ ...matching, urgentHours: e.target.value })} />
        </div>
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Fiabilité, badges et heures</div>
        <Hint>Seuils des badges « Top Extra » et « Établissement recommandé », indice de fiabilité, tolérance sur les heures et poids de l'historique en recherche urgente.</Hint>
        {([
          ['lastMinuteCancelHours', 'Désistement « de dernière minute » (heures avant le début)', 'Un désistement à moins de ce délai pèse dans l’indice de fiabilité.', 1],
          ['minMissionsForIndex', 'Missions engagées minimum avant d’afficher l’indice', 'Un extra qui débute n’est pas jugé sur une ou deux missions.', 1],
          ['topMinMissions', 'Top Extra : missions réussies minimum', '', 1],
          ['topMinAverage', 'Top Extra : moyenne minimale (sur 5)', 'Moyenne brute des avis révélés.', 0.1],
          ['topMinRatings', 'Top Extra : avis minimum', '', 1],
          ['topMaxLatePct', 'Top Extra : part maximale de retards (%)', '0 : aucun retard toléré.', 1],
          ['recoMinAverage', 'Établissement recommandé : moyenne minimale (sur 5)', '', 0.1],
          ['recoMinRatings', 'Établissement recommandé : avis minimum', '', 1],
          ['recoValidationHours', 'Délai de validation des heures (heures)', 'Heures validées dans ce délai après la fin du service.', 1],
          ['recoMinOnTimePct', 'Établissement recommandé : part de validations dans le délai (%)', '', 1],
          ['hoursToleranceMin', 'Tolérance sur les heures pointées (minutes)', 'Au-delà, on parle de divergence : ticket automatique en cas de dépassement, contestation possible sinon.', 1],
          ['urgentMultiplier', 'Poids de l’historique et de la fiabilité en recherche urgente (multiplicateur)', '0 : ignorés. 2 : ils pèsent deux fois leur poids habituel.', 0.5],
          ['reliabilityPrior', 'Fiabilité supposée d’un extra sans historique (0 à 1)', 'Un nouvel entrant n’est ni favorisé ni pénalisé.', 0.05],
          ['reliabilityPriorWeight', 'Poids de cette hypothèse (en missions)', '', 1],
        ] as const).map(([key, label, hint, step]) => (
          <div key={key}>
            <label className="field-label" htmlFor={`rel-${key}`}>{label}</label>
            <input id={`rel-${key}`} className="input" type="number" step={step} min={0} value={reliability[key]} onChange={(e) => setReliability({ ...reliability, [key]: e.target.value })} />
            {hint && <Hint>{hint}</Hint>}
          </div>
        ))}
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Notation et modération</div>
        <Hint>Les critères et les tags se gèrent dans la page « Notation ». Aucune sanction n'est automatique : les seuils ci-dessous alertent le support.</Hint>
        {([
          ['revealAfterDays', 'Délai de révélation d’un avis resté seul (jours)', 'Un avis reste caché jusqu’à ce que l’autre partie ait noté (double aveugle), ou ce nombre de jours.', 1],
          ['lowScoreThreshold', 'Seuil d’avis bas (moyenne sur 5)', 'En dessous, un motif est obligatoire et le support est alerté.', 0.1],
          ['consecutiveLowAlert', 'Avis bas consécutifs avant alerte', 'Nombre d’avis bas d’affilée pour une même personne ou un même établissement.', 1],
          ['priorScore', 'Note de départ du lissage', 'Un nouvel entrant démarre à cette note fictive, pour qu’un seul avis ne l’écrase pas.', 0.1],
          ['priorWeight', 'Poids de la note de départ (en nombre d’avis)', '0 : moyenne brute. Plus il est élevé, plus il faut d’avis pour s’éloigner de la note de départ.', 1],
          ['minRatingsToDisplay', 'Avis minimum avant d’afficher une note', 'En dessous, le profil s’affiche « Nouveau » sans note.', 1],
          ['minTagCount', 'Citations minimum d’un tag pour l’afficher', 'Seuls les tags positifs récurrents sont publics.', 1],
          ['lateToleranceMin', 'Tolérance de retard au pointage (minutes)', 'Au-delà, la note de ponctualité baisse par paliers de 10, 20 puis 40 minutes.', 1],
        ] as const).map(([key, label, hint, step]) => (
          <div key={key}>
            <label className="field-label" htmlFor={`r-${key}`}>{label}</label>
            <input id={`r-${key}`} className="input" type="number" step={step} min={0} value={ratings[key]} onChange={(e) => setRatings({ ...ratings, [key]: e.target.value })} />
            <Hint>{hint}</Hint>
          </div>
        ))}
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Société destinataire des factures</div>
          <span className={`badge ${companyComplete ? 'badge-available' : 'badge-pending'}`}>{companyComplete ? 'Complet' : 'À compléter'}</span>
        </div>
        <Hint>Ces coordonnées figurent comme destinataire sur chaque facture d'apporteur. Tant qu'elles sont incomplètes, aucun apporteur ne peut demander de versement.</Hint>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          <input className="input" placeholder="Raison sociale" value={company.legalName} onChange={(e) => setC({ legalName: e.target.value })} />
          <input className="input" placeholder="SIRET (14 chiffres)" value={company.siret} onChange={(e) => setC({ siret: e.target.value })} />
          <input className="input" placeholder="Adresse" value={company.address} onChange={(e) => setC({ address: e.target.value })} />
          <input className="input" placeholder="Code postal" value={company.postalCode} onChange={(e) => setC({ postalCode: e.target.value })} />
          <input className="input" placeholder="Ville" value={company.city} onChange={(e) => setC({ city: e.target.value })} />
          <input className="input" placeholder="N° de TVA intracommunautaire" value={company.vatNumber} onChange={(e) => setC({ vatNumber: e.target.value })} />
        </div>
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Notifications du back-office</div>
        <Hint>Cette adresse reçoit un e-mail quand un utilisateur écrit au support, qu'un message est signalé ou qu'une alerte de partage de coordonnées se déclenche. L'e-mail contient un lien vers le back-office, jamais le contenu. Vide : aucun e-mail.</Hint>
        <label className="field-label" htmlFor="support-email">Adresse de réception du support</label>
        <input id="support-email" className="input" type="email" placeholder="support@exemple.fr" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} />
      </div>

      {message && <div className={message.kind === 'ok' ?  'badge badge-available' : 'error-banner'} style={{ padding: '10px 14px' }}>{message.text}</div>}

      <button className="btn btn-primary" style={{ width: 'auto', alignSelf: 'flex-start', padding: '10px 20px' }} disabled={saving}>
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </form>
  );
}
